import { createPortal } from 'react-dom'
import { X, Brain, Calendar, CheckSquare, MessageSquare } from 'lucide-react'
import { useEffect, useState } from 'react'

const WELCOME_LS_KEY = 'welcome_shown'

export function WelcomeSheet() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (localStorage.getItem(WELCOME_LS_KEY)) return
    const t = setTimeout(() => setShow(true), 500)
    return () => clearTimeout(t)
  }, [])

  const handleClose = () => {
    localStorage.setItem(WELCOME_LS_KEY, '1')
    setShow(false)
  }

  if (!show) return null

  // Через портал в body: внутри страницы календаря лист уходил под таб-бар —
  // z-index внутри чужого контекста наложения снаружи ничего не значит.
  return createPortal(
    <div className="fixed inset-0 flex flex-col justify-end" style={{ zIndex: 60 }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div
        className="relative bg-surface-2 rounded-t-[20px] flex flex-col animate-slideUp"
        style={{
          maxHeight: '85vh',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 text-ink-2 hover:text-ink transition-colors z-10"
          aria-label="Закрыть"
        >
          <X size={20} />
        </button>

        <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4 space-y-4">
          <div className="space-y-2">
            <h2 className="text-h2 text-ink font-display">Больше ничего не теряется</h2>
            <p className="text-base text-ink-1 leading-relaxed">
              Помогаю с делами и вопросами, запоминаю важное, напоминаю вовремя
            </p>
          </div>

          <div className="space-y-3">
            <FeatureRow icon={<Calendar size={20} />} title="Календарь" desc="Напоминания, задачи и встречи в одном месте" />
            <FeatureRow icon={<Brain size={20} />} title="Память" desc="Что я о тебе помню" />
            <FeatureRow icon={<CheckSquare size={20} />} title="Задачи" desc="Список дел под рукой" />
            <FeatureRow icon={<MessageSquare size={20} />} title="История" desc="Вся переписка здесь" />
          </div>
        </div>

        <div
          className="sticky bottom-0 bg-surface-2 px-5 py-4"
          style={{
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          <button
            onClick={handleClose}
            className="w-full bg-accent text-white text-base font-medium py-3 rounded-xl transition-transform active:scale-[0.98]"
          >
            Понятно
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function FeatureRow({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="flex items-center justify-center shrink-0 rounded-full"
        style={{
          width: 36,
          height: 36,
          backgroundColor: 'rgba(74, 108, 247, 0.12)',
          color: '#4A6CF7',
        }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="text-xs text-ink-2 leading-relaxed">{desc}</p>
      </div>
    </div>
  )
}
